import { AssetClassLevel } from '@generated/graphql';
import {
  ASSET_CLASS_FIELD_BY_LEVEL,
  type AssetClassBranch,
  type FinancialProduct,
} from '@interfaces/financialProduct';
import { groupedAssetClassIdForChild } from '@ovComponents/3-pattern/securitiesSelectionTable/fundTargetPercentage';
import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/types';
import { round, sumBy } from 'lodash-es';
import { useMemo } from 'react';

export {
  type FinancialProductAssetClassGroup,
  groupFinancialProductsByAssetClassLevel,
  UNCATEGORIZED_ASSET_CLASS_GROUP_KEY,
} from '@ovComponents/3-pattern/securitySelect/utils/groupFinancialProductsByAssetClassLevel';

/**
 * Returns true when `assetClassBranches` contains a branch whose asset class
 * ID matches the cash financial product's asset class at the given level.
 * Used to gate the "+ Add Cash" button: if there is no matching branch, adding
 * a cash security would create an unassigned row.
 */
export const hasCashAssetClassBranch = (
  cashProduct: FinancialProduct | undefined | null,
  assetClassBranches: AssetClassBranch[],
  assetClassLevel: string | undefined,
): boolean => {
  if (!cashProduct) return false;
  const field =
    ASSET_CLASS_FIELD_BY_LEVEL[assetClassLevel as AssetClassLevel] ??
    ASSET_CLASS_FIELD_BY_LEVEL[AssetClassLevel.Primary];
  const cashAssetClassId = cashProduct[field]?.id;
  if (!cashAssetClassId) return false;
  return assetClassBranches.some((b) => b.assetClass?.id === cashAssetClassId);
};

export type AssetClassGroup = {
  branch: AssetClassBranch;
  securities: Security[];
};

export type UseAssetClassGroupingResult = {
  groupedSecurities: AssetClassGroup[] | null;
  unassignedSecurities: Security[];
  hasInvalidGroupTotal: boolean;
};

export type TaxRankValidationResult = {
  securitiesWithRanks: Security[];
  hasDuplicateTaxRanks: boolean;
  hasGapsOrInvalidStart: boolean;
};

/**
 * Tax ranks are validated globally across the entire flat array, not per
 * asset-class group. The backend consumes the flat `children` array and
 * expects a globally unique, gap-free sequence starting at 1, regardless of
 * how securities are grouped visually.
 */
export const getTaxRankValidation = (
  securities: Security[],
  isAssetClass: boolean,
): TaxRankValidationResult => {
  let nextTaxRank = 1;
  const securitiesWithRanks = isAssetClass
    ? securities.map((security) => {
        if (security.financialProduct.isCash) {
          return security;
        }

        const normalizedSecurity = {
          ...security,
          taxRank: security.taxRank ?? nextTaxRank,
        };
        nextTaxRank += 1;
        return normalizedSecurity;
      })
    : securities;

  const ranks = securitiesWithRanks
    .filter(
      (security) =>
        !security.financialProduct.isCash && security.taxRank != null,
    )
    .map((security) => Number(security.taxRank))
    .filter((rank): rank is number => Number.isInteger(rank) && rank > 0);

  const distinctRanks = Array.from(new Set(ranks)).sort((a, b) => a - b);
  const hasDuplicateTaxRanks = ranks.length !== distinctRanks.length;

  let hasGapsOrInvalidStart = false;
  if (distinctRanks.length > 0) {
    hasGapsOrInvalidStart = distinctRanks[0] !== 1;
    for (let index = 1; index < distinctRanks.length; index += 1) {
      if (distinctRanks[index] !== distinctRanks[index - 1] + 1) {
        hasGapsOrInvalidStart = true;
        break;
      }
    }
  }

  return {
    securitiesWithRanks,
    hasDuplicateTaxRanks,
    hasGapsOrInvalidStart,
  };
};

/**
 * Returns the sell-priority rank for a security at `index`, using its explicit
 * `taxRank` when set or the positional rank among non-cash rows otherwise.
 */
export const getEffectiveTaxRankAtIndex = (
  securities: Security[],
  index: number,
  isCashSecurity: (security: Security) => boolean,
): number | undefined => {
  const security = securities[index];
  if (!security || isCashSecurity(security)) return undefined;
  if (security.taxRank != null) return Number(security.taxRank);

  let rank = 0;
  for (let i = 0; i <= index; i += 1) {
    if (!isCashSecurity(securities[i])) rank += 1;
  }
  return rank;
};

/**
 * Closes tax-rank gaps after removal by decrementing ranks above the removed
 * security. Row order is unchanged so sell priority is preserved when ranks
 * were customized via swap/edit. Cash rows keep taxRank cleared.
 */
export const compactTaxRanksAfterRemoval = (
  securities: Security[],
  isCashSecurity: (security: Security) => boolean,
  removedTaxRank: number | undefined,
): Security[] =>
  securities.map((security) => {
    if (isCashSecurity(security)) {
      return { ...security, taxRank: undefined };
    }

    const rank =
      security.taxRank != null ? Number(security.taxRank) : undefined;
    if (removedTaxRank != null && rank != null && rank > removedTaxRank) {
      return { ...security, taxRank: rank - 1 };
    }

    return rank != null ? { ...security, taxRank: rank } : security;
  });

/** Groups securities under each asset-class branch (ASSET_CLASS strategy). Used by AddModelPortfolioSecurities and ModelPortfolioAssetAllocation. */
export const useAssetClassGrouping = (
  isAssetClass: boolean,
  securities: Security[],
  assetClassBranches: AssetClassBranch[],
  assetClassLevel: AssetClassLevel | string | undefined,
): UseAssetClassGroupingResult => {
  const groupedSecurities = useMemo((): AssetClassGroup[] | null => {
    if (!isAssetClass) return null;
    return assetClassBranches
      .filter((branch) => branch.assetClass?.id)
      .map((branch) => ({
        branch,
        securities: securities.filter(
          (s) =>
            groupedAssetClassIdForChild(s, assetClassLevel) ===
            branch.assetClass?.id,
        ),
      }));
  }, [isAssetClass, assetClassLevel, assetClassBranches, securities]);

  const unassignedSecurities = useMemo((): Security[] => {
    if (!isAssetClass || !groupedSecurities) return [];
    const assignedIds = new Set(
      groupedSecurities.flatMap(({ securities: gs }) =>
        gs.map((s) => s.financialProduct.id),
      ),
    );
    return securities.filter((s) => !assignedIds.has(s.financialProduct.id));
  }, [isAssetClass, groupedSecurities, securities]);

  const hasInvalidGroupTotal = useMemo((): boolean => {
    if (!isAssetClass || !groupedSecurities) return false;
    return groupedSecurities.some(({ securities: groupSecs }) => {
      if (groupSecs.length === 0) return true;
      return round(sumBy(groupSecs, 'percentage'), 2) !== 100;
    });
  }, [isAssetClass, groupedSecurities]);

  return { groupedSecurities, unassignedSecurities, hasInvalidGroupTotal };
};

/**
 * Distributes 100% equally across `groupSize` items.
 * The last item absorbs the floating-point remainder so the sum is exactly 100.
 */
export const computeAutoTarget = (groupSize: number): number[] => {
  if (groupSize === 0) return [];
  const equalShare = round(100 / groupSize, 2);
  const sumOfOthers = round(equalShare * (groupSize - 1), 2);
  return [
    ...new Array(groupSize - 1).fill(equalShare),
    round(100 - sumOfOthers, 2),
  ];
};

/**
 * Returns true when every group sums to exactly 100% and no securities are
 * unassigned.
 */
export const validateGroupAllocations = (
  groupedSecurities: AssetClassGroup[],
  unassignedCount: number,
): boolean => {
  if (unassignedCount > 0) return false;
  for (const { securities: groupSecs } of groupedSecurities) {
    if (groupSecs.length === 0) return false;
    const groupTotal = round(sumBy(groupSecs, 'percentage'), 2);
    if (groupTotal !== 100) return false;
  }
  return true;
};
